import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { Filter, X, Search } from 'lucide-react';
import StarRating from '../components/StarRating';
import { useListings, useDebouncedValue, matchesSearch } from '../hooks/useListings';
import { useLanguage } from '../context/LanguageContext';

import { supabase } from '../supabaseClient';
import { fetchBrokerRatingSummary } from '../lib/brokerReviews';
import { rotateForSession } from '../lib/boostRotation';
import { formatMapPrice } from '../lib/formatPrice';
import { useBodyScrollLock } from '../lib/bodyScrollLock';
import PropertyCard from '../components/PropertyCard';
import './ListingsPage.css';

const COORDS = {
  // Cities
  'Limassol':         [34.6841, 33.0392],
  'Nicosia':          [35.1856, 33.3823],
  'Paphos':           [34.7754, 32.4219],
  'Larnaca':          [34.9003, 33.6233],
  'Famagusta':        [35.1213, 33.9408],
  'Ayia Napa':        [34.9843, 34.0000],
  'Agia Napa':        [34.9843, 34.0000],
  'Protaras':         [35.0122, 34.0557],
  'Paralimni':        [35.0380, 33.9830],
  // Limassol neighbourhoods
  'Germasogeia':      [34.6823, 33.0823],
  'Mesa Geitonia':    [34.6750, 33.0550],
  'Agios Tychon':     [34.6650, 33.0700],
  'Polemidia':        [34.6950, 33.0200],
  'Neapolis':         [34.6780, 33.0450],
  'Marina':           [34.6700, 33.0480],
  'Zakaki':           [34.6900, 33.0100],
  'Ypsonas':          [34.7050, 32.9900],
  // Nicosia neighbourhoods
  'Strovolos':        [35.1280, 33.3620],
  'Engomi':           [35.1680, 33.3320],
  'Latsia':           [35.1050, 33.3500],
  'Geri':             [35.1400, 33.4500],
  'Aglandjia':        [35.1600, 33.4000],
  'Anthoupoli':       [35.1750, 33.3200],
  // Paphos neighbourhoods
  'Kato Paphos':      [34.7580, 32.4100],
  'Kato Pafos':       [34.7580, 32.4100],
  'Chloraka':         [34.7950, 32.4050],
  'Peyia':            [34.8384, 32.3500],
  'Pegia':            [34.8384, 32.3500],
  'Coral Bay':        [34.8384, 32.3944],
  'Mesa Chorio':      [34.8200, 32.4200],
  'Tala':             [34.8100, 32.4000],
  'Kissonerga':       [34.8050, 32.3850],
  // Larnaca neighbourhoods
  'Mackenzie':        [34.8975, 33.6367],
  'Mckenzie':         [34.8975, 33.6367],
  'Finikoudes':       [34.9150, 33.6350],
  'Aradippou':        [34.9550, 33.5900],
  'Aradipou':         [34.9550, 33.5900],
  'Livadia':          [34.9300, 33.6100],
  'Oroklini':         [34.9600, 33.6600],
  // Famagusta district
  'Deryneia':         [35.0500, 33.9600],
};

const CYPRUS_BOUNDS = [[34.4, 32.0],[35.8, 34.7]];

const FILTER_CITIES = [
  'Nicosia', 'Limassol', 'Larnaca', 'Paphos',
  'Ayia Napa', 'Protaras', 'Paralimni',
];

const BEDS_CHIPS = [
  { value: '',  label: 'Any' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5+' },
];

const MIN_PRICE_PRESETS_SALE = [
  { value: '',        label: 'No min' },
  { value: '50000',   label: '€50k' },
  { value: '100000',  label: '€100k' },
  { value: '200000',  label: '€200k' },
  { value: '350000',  label: '€350k' },
  { value: '500000',  label: '€500k' },
  { value: '1000000', label: '€1M' },
];

const MAX_PRICE_PRESETS_SALE = [
  { value: '',        label: 'No max' },
  { value: '100000',  label: '€100k' },
  { value: '200000',  label: '€200k' },
  { value: '350000',  label: '€350k' },
  { value: '500000',  label: '€500k' },
  { value: '1000000', label: '€1M' },
  { value: '2000000', label: '€2M' },
];

const MIN_PRICE_PRESETS_RENT = [
  { value: '',     label: 'No min' },
  { value: '300',  label: '€300/mo' },
  { value: '500',  label: '€500/mo' },
  { value: '1000', label: '€1000/mo' },
  { value: '2000', label: '€2000/mo' },
];

const MAX_PRICE_PRESETS_RENT = [
  { value: '',     label: 'No max' },
  { value: '500',  label: '€500/mo' },
  { value: '1000', label: '€1000/mo' },
  { value: '2000', label: '€2000/mo' },
  { value: '5000', label: '€5000/mo' },
];

function PropertyMap({ listings }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (!mapRef.current) return;
    const L = window.L || require('leaflet');
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [34.9, 33.05], zoom: 8, minZoom: 8, maxZoom: 16,
        maxBounds: CYPRUS_BOUNDS, maxBoundsViscosity: 1.0, zoomControl: false,
      });
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap © CARTO', subdomains: 'abcd', maxZoom: 19,
      }).addTo(mapInstanceRef.current);
      L.control.zoom({ position: 'bottomright' }).addTo(mapInstanceRef.current);
    }
  }, []);

  useEffect(() => {
    const L = window.L || require('leaflet');
    const map = mapInstanceRef.current;
    if (!map) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    listings.forEach((listing, i) => {
     const coords = COORDS[listing.region] || COORDS[listing.city] ||
  COORDS[Object.keys(COORDS).find(k => k.toLowerCase() === (listing.region || '').toLowerCase())] ||
  COORDS[Object.keys(COORDS).find(k => k.toLowerCase() === (listing.city || '').toLowerCase())];
      if (!coords) return;
      const lat = coords[0] + (i % 3 - 1) * 0.005;
      const lng = coords[1] + (i % 2 === 0 ? 0.006 : -0.006);
      const price = formatMapPrice(listing.price, listing.priceType);
      const icon = L.divIcon({
        className: '',
        html: `<div class="lp-pin-wrap"><div class="lp-pin-circle">${price}</div><div class="lp-pin-tooltip"><div class="lp-pin-tooltip__title">${listing.title}</div><div class="lp-pin-tooltip__sub">${listing.city}, ${listing.region}</div><div class="lp-pin-tooltip__price">${price}</div><div class="lp-pin-tooltip__cta">${document.documentElement.lang === 'el' ? 'Δείτε ακίνητο' : 'View property'}</div></div></div>`,
        iconSize: [64, 32], iconAnchor: [32, 16],
      });
      const marker = L.marker([lat, lng], { icon }).addTo(map);
      marker.on('click', () => navigate(`/property/${listing.id}`));
      markersRef.current.push(marker);
    });
  }, [listings, navigate]);

  return <div className="lp-map"><div ref={mapRef} className="lp-map__container" /></div>;
}

function buildRankedList(listings, sortBy) {
  const now = new Date();
  const isActiveBoost = l =>
    l.is_boosted && l.boost_expires_at && new Date(l.boost_expires_at) > now;

  const applySort = (arr) => {
    switch (sortBy) {
      case 'price-asc':   return [...arr].sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'price-desc':  return [...arr].sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'newest':      return [...arr].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      case 'oldest':      return [...arr].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case 'size-large':  return [...arr].sort((a, b) => (b.size || 0) - (a.size || 0));
      case 'size-small':  return [...arr].sort((a, b) => (a.size || 0) - (b.size || 0));
      case 'beds-most':   return [...arr].sort((a, b) => (b.bedrooms || 0) - (a.bedrooms || 0));
      default:            return [...arr].sort((a, b) => (b.qualityScore || 0) - (a.qualityScore || 0));
    }
  };

  // For explicit sorts, apply to all listings so the sort is unambiguous
  if (sortBy !== 'newest') {
    return applySort(listings);
  }

  // Default 'newest': interleave boosted listings for visibility
  const boosted = listings.filter(isActiveBoost);
  const organic = listings.filter(l => !isActiveBoost(l));

  const boostFinalScore = (l) => {
    const qs = l.qualityScore || 0;
    const daysRemaining = (new Date(l.boost_expires_at) - now) / 86400000;
    const daysIntoBoost = Math.max(0, 30 - daysRemaining);
    const timeDecay = Math.max(0.5, 1.0 - (daysIntoBoost / 30) * 0.5);
    return qs * (1 + 0.5 * timeDecay);
  };

  const sortedOrganic = applySort(organic);
  const rotated = rotateForSession(
    [...boosted].sort((a, b) => boostFinalScore(b) - boostFinalScore(a))
  );

  const result = [];
  let bi = 0, oi = 0;
  while (oi < sortedOrganic.length) {
    if (bi < rotated.length) result.push(rotated[bi++]);
    for (let j = 0; j < 8 && oi < sortedOrganic.length; j++) result.push(sortedOrganic[oi++]);
  }
  while (bi < rotated.length) result.push(rotated[bi++]);

  return result;
}

export default function ListingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { listings: allListings, loading } = useListings();
  const { t } = useLanguage();

  const navigate = useNavigate();
  const { brokerId } = useParams();

  const [filtered, setFiltered]         = useState([]);

  const [brokerInfo, setBrokerInfo]     = useState(null);
  const [brokerSummary, setBrokerSummary] = useState(null);

  // URL params from SearchBar use type=buy|rent; internal state uses sale|rent|all
  const initialPriceType = (() => {
    const t = searchParams.get('type');
    if (t === 'buy' || t === 'sale') return 'sale';
    if (t === 'rent') return 'rent';
    return 'all';
  })();
  const initialPropertyType = searchParams.get('propertyType') || searchParams.get('propType') || 'all';

  const [priceType, setPriceType]       = useState(initialPriceType);
  const [propertyType, setPropertyType] = useState(initialPropertyType);
  const [sortBy, setSortBy]             = useState('newest');
  const [searchInput, setSearchInput]   = useState(searchParams.get('q') || '');
  const [companies, setCompanies]       = useState([]);
  const [company, setCompany]           = useState(searchParams.get('company') || '');
  const [cityFilter, setCityFilter]     = useState(searchParams.get('city') || '');
  const [bedsFilter, setBedsFilter]     = useState(searchParams.get('beds') || '');
  const [maxPriceFilter, setMaxPriceFilter] = useState(searchParams.get('maxPrice') || '');
  const [minPriceFilter, setMinPriceFilter] = useState(searchParams.get('minPrice') || '');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(searchInput, 250);

  // Lock body scroll while the mobile filter sheet is open. Uses the shared
  // holder-set lock so concurrent lockers (drawer, modal) can't poison each
  // other's restore value and leave the body stuck at overflow:hidden.
  useBodyScrollLock(mobileFiltersOpen);

  const activeFilterCount =
    (priceType !== 'all' ? 1 : 0) +
    (propertyType !== 'all' ? 1 : 0) +
    (company ? 1 : 0) +
    (cityFilter ? 1 : 0) +
    (bedsFilter ? 1 : 0) +
    (maxPriceFilter ? 1 : 0) +
    (minPriceFilter ? 1 : 0);

  const handleClearFilters = () => {
    setPriceType('all');
    setPropertyType('all');
    setCompany('');
    setCityFilter('');
    setBedsFilter('');
    setMaxPriceFilter('');
    setMinPriceFilter('');
    setSortBy('newest');
  };

  const minPricePresets = priceType === 'rent' ? MIN_PRICE_PRESETS_RENT : MIN_PRICE_PRESETS_SALE;
  const maxPricePresets = priceType === 'rent' ? MAX_PRICE_PRESETS_RENT : MAX_PRICE_PRESETS_SALE;

  // Lock body scroll while the mobile filter sheet is open
  useEffect(() => {
    if (mobileFiltersOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileFiltersOpen]);

  // Fetch partner companies for filter dropdown
  useEffect(() => {
    supabase.from('agencies').select('name').order('name', { ascending: true })
      .then(({ data }) => {
        const names = (data || []).map(a => a.name).filter(Boolean);
        setCompanies(names);
      });
  }, []);

  // Fetch broker data when in broker-filter mode
  useEffect(() => {
    if (!brokerId) return;
    supabase.from('brokers').select('*').eq('id', brokerId).single().then(({ data }) => {
      setBrokerInfo(data || null);
    });
    fetchBrokerRatingSummary(brokerId).then(setBrokerSummary);
  }, [brokerId]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (debouncedSearch) params.set('q', debouncedSearch); else params.delete('q');
    // Write type=buy for sale (matches SearchBar's URL semantics)
    if (priceType === 'sale') params.set('type', 'buy');
    else if (priceType === 'rent') params.set('type', 'rent');
    else params.delete('type');
    if (propertyType !== 'all') params.set('propertyType', propertyType); else params.delete('propertyType');
    params.delete('propType'); // clean up legacy param name
    if (company) params.set('company', company); else params.delete('company');
    if (cityFilter) params.set('city', cityFilter); else params.delete('city');
    if (bedsFilter) params.set('beds', bedsFilter); else params.delete('beds');
    if (maxPriceFilter) params.set('maxPrice', maxPriceFilter); else params.delete('maxPrice');
    if (minPriceFilter) params.set('minPrice', minPriceFilter); else params.delete('minPrice');
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, priceType, propertyType, company, cityFilter, bedsFilter, maxPriceFilter, minPriceFilter]); // eslint-disable-line

  useEffect(() => {
    let result = [...allListings];

    // In broker mode, pre-filter to this broker's listings
    if (brokerId && brokerInfo?.email) {
      result = result.filter(l => l.contact_email === brokerInfo.email);
    }

    if (priceType !== 'all') result = result.filter(l => {
      if (priceType === 'rent') return l.priceType === 'per month';
      if (priceType === 'sale') return !l.priceType || l.priceType !== 'per month';
      return true;
    });
    if (propertyType !== 'all') result = result.filter(l => l.type?.toLowerCase() === propertyType.toLowerCase());
    if (company) result = result.filter(l => l.agent?.agency === company);
    if (cityFilter) {
      const c = cityFilter.toLowerCase();
      result = result.filter(l => (l.city || '').toLowerCase() === c);
    }
    if (bedsFilter) {
      const n = parseInt(bedsFilter, 10);
      // "5" chip filters for 5+ bedrooms; lower chips are exact-match
      if (!isNaN(n)) {
        result = n >= 5
          ? result.filter(l => (l.bedrooms || 0) >= n)
          : result.filter(l => Number(l.bedrooms) === n);
      }
    }
    if (maxPriceFilter) {
      const n = parseInt(maxPriceFilter, 10);
      if (!isNaN(n)) result = result.filter(l => (l.price || 0) <= n);
    }
    if (minPriceFilter) {
      const n = parseInt(minPriceFilter, 10);
      if (!isNaN(n)) result = result.filter(l => (l.price || 0) >= n);
    }
    result = result.filter(l => matchesSearch(l, debouncedSearch));

    setFiltered(buildRankedList(result, sortBy));
  }, [allListings, priceType, propertyType, sortBy, debouncedSearch, brokerId, brokerInfo, company, cityFilter, bedsFilter, maxPriceFilter, minPriceFilter]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);


  return (
    <main className="listings-page">

      {/* ── Broker banner (only when coming from a broker profile) ── */}
      {brokerId && (
        <div className="lp-broker-banner">
          <div className="lp-broker-banner__inner">
            <button
              className="lp-broker-back"
              onClick={() => navigate(`/find-broker/${brokerId}`)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Back to profile
            </button>
            <div className="lp-broker-info">
              <h2 className="lp-broker-name">
                {brokerInfo ? `Listings by ${brokerInfo.name}` : 'Broker Listings'}
              </h2>
              <div className="lp-broker-stats">
                <span>{filtered.length} active {filtered.length === 1 ? 'listing' : 'listings'}</span>
                {brokerSummary?.count > 0 && (
                  <>
                    <span className="lp-broker-sep">·</span>
                    <StarRating value={brokerSummary.average} readonly size="small" showValue />
                    <span className="lp-broker-review-ct">({brokerSummary.count} {brokerSummary.count === 1 ? 'review' : 'reviews'})</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="listings-page__header">
        <div className="listings-page__header-inner">
          <div>
            <h1 className="listings-page__title">
              {brokerId && brokerInfo
                ? brokerInfo.name
                : debouncedSearch
                  ? t('listings.propertiesIn').replace('{q}', debouncedSearch)
                  : t('listings.pageTitle')}
            </h1>
            <p className="listings-page__count">
              <strong>{filtered.length}</strong>{' '}
              {brokerId ? (filtered.length === 1 ? 'property' : 'properties') : t('listings.propertiesFound')}
            </p>
          </div>
        </div>
      </div>

      <div className="listings-page__body">
        {/* Mobile-only toolbar: Filters button + sort select */}
        <div className="listings-page__mobileToolbar">
          <button
            type="button"
            className="listings-page__mobileFilterBtn"
            onClick={() => setMobileFiltersOpen(true)}
          >
            <Filter size={16} />
            <span>{t('listings.filtersBtn') || 'Filters'}</span>
            {activeFilterCount > 0 && (
              <span className="listings-page__filterBadge">{activeFilterCount}</span>
            )}
          </button>
          <select
            className="listings-page__mobileSortSelect"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            aria-label={t('listings.sortBy')}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="price-asc">Price: low to high</option>
            <option value="price-desc">Price: high to low</option>
            <option value="size-large">Size: largest first</option>
            <option value="size-small">Size: smallest first</option>
            <option value="beds-most">Most bedrooms</option>
          </select>
        </div>

        {/* Free-text search — sits above the filter chips, styled to match the
            homepage hero SearchBar field treatment. */}
        <form
          className="lp-search-wrap"
          onSubmit={e => e.preventDefault()}
          role="search"
        >
          <div className="lp-search-field">
            <Search size={16} className="lp-search-icon" />
            <input
              type="text"
              className="lp-search-input"
              placeholder={brokerId ? "Search within this broker's listings…" : 'Search by city, area, or property name...'}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            {searchInput !== '' && (
              <button
                type="button"
                className="lp-search-clear"
                onClick={() => setSearchInput('')}
                aria-label="Clear search"
              >×</button>
            )}
          </div>
          <button type="submit" className="lp-search-submit">Search</button>
        </form>

        {/* Desktop filters (hidden on mobile via CSS) */}
        <div className="listings-page__filters listings-page__filters--desktop">
          <div className="filter-group">
            <label>{t('listings.listingType')}</label>
            <div className="filter-chips">
              {['all', 'sale', 'rent'].map(type => (
                <button key={type} className={`filter-chip ${priceType === type ? 'filter-chip--active' : ''}`} onClick={() => setPriceType(type)}>
                  {type === 'all' ? t('listings.filters.all') : type === 'sale' ? t('listings.filters.forSale') : t('listings.filters.forRent')}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <label>{t('listings.propertyType')}</label>
            <div className="filter-chips">
              {['all', 'house', 'apartment', 'villa'].map(type => (
                <button key={type} className={`filter-chip ${propertyType === type ? 'filter-chip--active' : ''}`} onClick={() => setPropertyType(type)}>
                  {type === 'all' ? t('listings.filters.all') : t(`listings.filters.${type}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <label>CITY</label>
            <select className="filter-select" value={cityFilter} onChange={e => setCityFilter(e.target.value)}>
              <option value="">Any city</option>
              {FILTER_CITIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>BEDROOMS</label>
            <div className="filter-chips">
              {BEDS_CHIPS.map(opt => (
                <button
                  key={opt.value || 'any'}
                  className={`filter-chip ${bedsFilter === opt.value ? 'filter-chip--active' : ''}`}
                  onClick={() => setBedsFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <label>MIN PRICE</label>
            <select className="filter-select" value={minPriceFilter} onChange={e => setMinPriceFilter(e.target.value)}>
              {minPricePresets.map(opt => (
                <option key={opt.value || 'any'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>MAX PRICE</label>
            <select className="filter-select" value={maxPriceFilter} onChange={e => setMaxPriceFilter(e.target.value)}>
              {maxPricePresets.map(opt => (
                <option key={opt.value || 'any'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {companies.length > 0 && (
            <div className="filter-group">
              <label>COMPANY</label>
              <select className="filter-select" value={company} onChange={e => setCompany(e.target.value)}>
                <option value="">All companies</option>
                {companies.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="filter-group filter-group--right">
            <label>{t('listings.sortBy')}</label>
            <select className="filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="price-asc">Price: low to high</option>
              <option value="price-desc">Price: high to low</option>
              <option value="size-large">Size: largest first</option>
              <option value="size-small">Size: smallest first</option>
              <option value="beds-most">Most bedrooms</option>
            </select>
          </div>
        </div>

        <div className="listings-page__layout">
          <div className="listings-page__map-col">
            <PropertyMap listings={filtered} />
            {/* Ad slot hidden for now — restore by removing `style={{ display: 'none' }}`. */}
            <div className="lp-ad-slot" style={{ display: 'none' }}>
              <div className="lp-ad-slot__label">{t('listings.advertisement')}</div>
              <div className="lp-ad-slot__title">{t('listings.placeAd')}</div>
              <div className="lp-ad-slot__sub">{t('listings.adSub')}</div>
              <a href="mailto:cosmoestatescyprus@gmail.com" style={{display:'inline-block',background:'#0E0E10',color:'white',fontSize:13,fontWeight:700,padding:'10px 20px',borderRadius:4,textDecoration:'none',flexShrink:0}}>{t('listings.advertise')}</a>
            </div>
          </div>
          <div className="listings-page__grid-col">
            {propertyType !== 'all' && (
              <div className="lp-active-filter">
                <span className="lp-active-filter__label">
                  Filtered by: <strong>{{
                    house: 'Houses', apartment: 'Apartments', villa: 'Villas',
                  }[propertyType] || propertyType}</strong>
                </span>
                <button
                  className="lp-active-filter__clear"
                  onClick={() => setPropertyType('all')}
                >
                  Clear ✕
                </button>
              </div>
            )}
            {company && (
              <div className="lp-active-filter">
                <span className="lp-active-filter__label">
                  Showing listings from: <strong>{company}</strong>
                </span>
                <button
                  className="lp-active-filter__clear"
                  onClick={() => setCompany('')}
                >
                  Clear ✕
                </button>
              </div>
            )}
            {loading ? (
              <div className="listings-page__grid">
                {[1,2,3,4].map(i => (
                  <div key={i} style={{ height: 320, background: '#F3F4F6', borderRadius: 16 }} />
                ))}
              </div>
            ) : filtered.length > 0 ? (
              <div className="listings-page__grid">
                {filtered.map((listing, i) => (
                  <PropertyCard key={listing.id} listing={listing} index={i} />
                ))}
              </div>
            ) : (
              <div className="listings-page__empty">
                <h3>{t('listings.noProperties')}</h3>
                {brokerId ? (
                  <p>This broker has no active listings{debouncedSearch ? ` matching "${debouncedSearch}"` : ''}.</p>
                ) : debouncedSearch ? (
                  <>
                    <p>No listings match &ldquo;{debouncedSearch}&rdquo;. Try a different search or clear filters.</p>
                    <button className="lp-clear-search-btn" onClick={() => setSearchInput('')}>
                      Clear search
                    </button>
                  </>
                ) : (
                  <p>{t('listings.adjustFilters')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      {mobileFiltersOpen && (
        <div
          className="lp-filterSheet__backdrop"
          onClick={() => setMobileFiltersOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t('listings.filtersBtn') || 'Filters'}
        >
          <div className="lp-filterSheet" onClick={(e) => e.stopPropagation()}>
            <header className="lp-filterSheet__header">
              <h3>{t('listings.filtersBtn') || 'Filters'}</h3>
              <button
                type="button"
                className="lp-filterSheet__close"
                onClick={() => setMobileFiltersOpen(false)}
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </header>
            <div className="lp-filterSheet__body">
              <div className="filter-group">
                <label>{t('listings.listingType')}</label>
                <div className="filter-chips">
                  {['all', 'sale', 'rent'].map(type => (
                    <button key={type} type="button" className={`filter-chip ${priceType === type ? 'filter-chip--active' : ''}`} onClick={() => setPriceType(type)}>
                      {type === 'all' ? t('listings.filters.all') : type === 'sale' ? t('listings.filters.forSale') : t('listings.filters.forRent')}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-group">
                <label>{t('listings.propertyType')}</label>
                <div className="filter-chips">
                  {['all', 'house', 'apartment', 'villa'].map(type => (
                    <button key={type} type="button" className={`filter-chip ${propertyType === type ? 'filter-chip--active' : ''}`} onClick={() => setPropertyType(type)}>
                      {type === 'all' ? t('listings.filters.all') : t(`listings.filters.${type}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-group">
                <label>CITY</label>
                <select className="filter-select" value={cityFilter} onChange={e => setCityFilter(e.target.value)}>
                  <option value="">Any city</option>
                  {FILTER_CITIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>BEDROOMS</label>
                <div className="filter-chips">
                  {BEDS_CHIPS.map(opt => (
                    <button
                      key={opt.value || 'any'}
                      type="button"
                      className={`filter-chip ${bedsFilter === opt.value ? 'filter-chip--active' : ''}`}
                      onClick={() => setBedsFilter(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-group">
                <label>MIN PRICE</label>
                <select className="filter-select" value={minPriceFilter} onChange={e => setMinPriceFilter(e.target.value)}>
                  {minPricePresets.map(opt => (
                    <option key={opt.value || 'any'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>MAX PRICE</label>
                <select className="filter-select" value={maxPriceFilter} onChange={e => setMaxPriceFilter(e.target.value)}>
                  {maxPricePresets.map(opt => (
                    <option key={opt.value || 'any'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {companies.length > 0 && (
                <div className="filter-group">
                  <label>COMPANY</label>
                  <select className="filter-select" value={company} onChange={e => setCompany(e.target.value)}>
                    <option value="">All companies</option>
                    {companies.map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="filter-group">
                <label>{t('listings.sortBy')}</label>
                <select className="filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="price-asc">Price: low to high</option>
                  <option value="price-desc">Price: high to low</option>
                  <option value="size-large">Size: largest first</option>
                  <option value="size-small">Size: smallest first</option>
                  <option value="beds-most">Most bedrooms</option>
                </select>
              </div>
            </div>
            <footer className="lp-filterSheet__footer">
              <button
                type="button"
                className="lp-filterSheet__clear"
                onClick={handleClearFilters}
              >
                Clear all
              </button>
              <button
                type="button"
                className="lp-filterSheet__apply"
                onClick={() => setMobileFiltersOpen(false)}
              >
                Show {filtered.length} {filtered.length === 1 ? 'listing' : 'listings'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </main>
  );
}
